import React, { useState, useEffect, useRef, useMemo } from 'react';
import './BannerEditorV2.scss';
import {
  TemplateManager,
  CanvasExporter,
  ZIndexManager,
  ElementPosition,
  AdTemplate
} from './editorUtils';
import SvgAdCanvas, { SvgAdCanvasHandle } from './SvgAdCanvas';

interface ColorSchemeElement {
  element: string;
  color: string;
}

interface Typography {
  headline_font?: string;
  body_font?: string;
  headline_size?: string;
  body_size?: string;
}

interface PlatformSize {
  name: string;
  width: number;
  height: number;
  aspect_ratio: string;
}

interface GeneratedContent {
  headline?: string;
  ad_copy?: string;
  cta?: string;
  hashtags?: string[];
  color_scheme?: ColorSchemeElement[];
  recommended_font?: string;
  story_emotion?: string;
  typography?: Typography;
  platform_sizes?: { [platform: string]: PlatformSize[] };
}

interface ImageRecommendation {
  url: string;
  alt_text?: string;
  score?: number;
  rationale?: string;
}

interface FirmDetails {
  name?: string;
  business_name?: string;
  industry?: string;
  brand_colors?: string[];
  dominant_color?: string;
  font_preferences?: string[];
  dominant_font?: string;
  logo_url?: string;
}

interface BannerEditorProps {
  generatedContent: GeneratedContent;
  recommendedImages: ImageRecommendation[];
  selectedImage: string | null;
  onImageSelect: (url: string) => void;
  firmDetails: FirmDetails | null;
  onDownload: () => void;
  onPublish: () => void;
  selectedPlatforms: string[];
}

// ElementPosition imported from editorUtils

interface AdElementsState {
  logo: ElementPosition;
  businessName: ElementPosition;
  headline: ElementPosition;
  copy: ElementPosition;
  cta: ElementPosition;
  hashtags: ElementPosition;
}

const DEFAULT_PLATFORM_SIZES = {
  Facebook: [
    { name: 'Feed Post', width: 1200, height: 630, aspect_ratio: '1.91:1' },
    { name: 'Square Post', width: 1080, height: 1080, aspect_ratio: '1:1' },
    { name: 'Story', width: 1080, height: 1920, aspect_ratio: '9:16' }
  ],
  Instagram: [
    { name: 'Feed Post', width: 1080, height: 1080, aspect_ratio: '1:1' },
    { name: 'Story', width: 1080, height: 1920, aspect_ratio: '9:16' },
    { name: 'Landscape', width: 1080, height: 566, aspect_ratio: '1.91:1' }
  ],
  LinkedIn: [
    { name: 'Sponsored Content', width: 1200, height: 627, aspect_ratio: '1.91:1' },
    { name: 'Square Post', width: 1080, height: 1080, aspect_ratio: '1:1' }
  ],
  Twitter: [
    { name: 'Tweet Image', width: 1200, height: 675, aspect_ratio: '16:9' },
    { name: 'Square', width: 1080, height: 1080, aspect_ratio: '1:1' }
  ]
};

const BannerEditorV2: React.FC<BannerEditorProps> = ({
  generatedContent,
  recommendedImages,
  selectedImage,
  onImageSelect,
  firmDetails,
  onDownload,
  onPublish,
  selectedPlatforms
}) => {
  const [activePlatform, setActivePlatform] = useState<string>(selectedPlatforms[0] || 'Facebook');
  const [activeSizeIndex, setActiveSizeIndex] = useState<number>(0);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [sampleImages, setSampleImages] = useState<ImageRecommendation[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // Content state
  const [headline, setHeadline] = useState(generatedContent.headline || '');
  const [adCopy, setAdCopy] = useState(generatedContent.ad_copy || '');
  const [ctaText, setCtaText] = useState(generatedContent.cta || '');
  const [hashtags, setHashtags] = useState<string[]>(generatedContent.hashtags || []);

  // Element positions and styles
  const [elements, setElements] = useState<AdElementsState>({
    logo: { x: 20, y: 20, width: 80, height: 80, rotation: 0, zIndex: 10 },
    businessName: { x: 120, y: 30, width: 250, height: 50, rotation: 0, zIndex: 11 },
    headline: { x: 20, y: 120, width: 300, height: 60, rotation: 0, zIndex: 12 },
    copy: { x: 20, y: 200, width: 300, height: 100, rotation: 0, zIndex: 13 },
    cta: { x: 20, y: 320, width: 200, height: 50, rotation: 0, zIndex: 14 },
    hashtags: { x: 20, y: 390, width: 300, height: 40, rotation: 0, zIndex: 15 }
  });

  // Font and color state
  const [headlineFont, setHeadlineFont] = useState(
    generatedContent.typography?.headline_font || firmDetails?.dominant_font || 'Arial'
  );
  const [headlineFontSize, setHeadlineFontSize] = useState(
    generatedContent.typography?.headline_size || '32px'
  );
  const [headlineColor, setHeadlineColor] = useState(
    generatedContent.color_scheme?.find(c => c.element === 'headline')?.color || '#000000'
  );

  const [copyFont, setCopyFont] = useState(
    generatedContent.typography?.body_font || firmDetails?.dominant_font || 'Arial'
  );
  const [copyFontSize, setCopyFontSize] = useState(
    generatedContent.typography?.body_size || '16px'
  );
  const [copyColor, setCopyColor] = useState(
    generatedContent.color_scheme?.find(c => c.element === 'ad_copy')?.color || '#000000'
  );

  const [ctaBackgroundColor, setCtaBackgroundColor] = useState(
    generatedContent.color_scheme?.find(c => c.element === 'cta_button')?.color || 
    firmDetails?.dominant_color || '#4169E1'
  );
  const [ctaTextColor, setCtaTextColor] = useState(
    generatedContent.color_scheme?.find(c => c.element === 'cta_text')?.color || '#FFFFFF'
  );
  const [ctaFont, setCtaFont] = useState(firmDetails?.dominant_font || 'Arial');
  const [ctaFontSize, setCtaFontSize] = useState('16px');

  const [hashtagsColor, setHashtagsColor] = useState('#4169E1');
  const [hashtagsFont, setHashtagsFont] = useState(firmDetails?.dominant_font || 'Arial');
  const [hashtagsFontSize, setHashtagsFontSize] = useState('14px');

  // Advanced features state
  const [zIndexOrder, setZIndexOrder] = useState<string[]>(['logo', 'businessName', 'headline', 'copy', 'cta', 'hashtags']);
  const [templates, setTemplates] = useState<AdTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgCanvasRef = useRef<SvgAdCanvasHandle>(null);

  // Business name styling
  const [businessNameColor, setBusinessNameColor] = useState(
    firmDetails?.dominant_color || '#000000'
  );
  const [businessNameFont, setBusinessNameFont] = useState(
    firmDetails?.dominant_font || 'Arial'
  );
  const [businessNameFontSize, setBusinessNameFontSize] = useState('24px');
  const containerRef = useRef<HTMLDivElement>(null);

  // Text alignment state
  const [businessNameAlign, setBusinessNameAlign] = useState<'left' | 'center' | 'right'>('center');
  const [headlineAlign, setHeadlineAlign] = useState<'left' | 'center' | 'right'>('center');
  const [copyAlign, setCopyAlign] = useState<'left' | 'center' | 'right'>('left');
  const [hashtagsAlign, setHashtagsAlign] = useState<'left' | 'center' | 'right'>('left');

  // Load templates on mount
  useEffect(() => {
    setTemplates(TemplateManager.getAllTemplates());
  }, []);

  // Get current canvas size based on selected platform and size
  const getCurrentCanvasSize = (): PlatformSize => {
    const platformSizes = generatedContent.platform_sizes?.[activePlatform] || 
                         DEFAULT_PLATFORM_SIZES[activePlatform as keyof typeof DEFAULT_PLATFORM_SIZES] || 
                         DEFAULT_PLATFORM_SIZES.Facebook;
    return platformSizes[activeSizeIndex] || platformSizes[0];
  };

  const canvasSize = getCurrentCanvasSize();

  // Fetch sample images
  useEffect(() => {
    const fetchSampleImages = async () => {
      setIsLoadingImages(true);
      try {
        const apiUrlBase = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/';
        const response = await fetch(`${apiUrlBase}v2/sample-images`);
        if (response.ok) {
          const data = await response.json();
          const transformedImages = data.map((img: any) => ({
            url: img.image_url || img.azure_blob_url,
            alt_text: img.llm_analysis?.description || 'Sample image',
            score: img.recommended_score || img.quality_score
          }));
          setSampleImages(transformedImages);
        }
      } catch (error) {
        console.error('Failed to fetch sample images:', error);
      } finally {
        setIsLoadingImages(false);
      }
    };

    fetchSampleImages();
  }, []);

  // Handle element position update
  const updateElementPosition = (elementKey: keyof AdElementsState, updates: Partial<ElementPosition>) => {
    setElements(prev => ({
      ...prev,
      [elementKey]: { ...prev[elementKey], ...updates }
    }));
  };

  // Handle element resize
  const handleElementResize = (elementKey: keyof AdElementsState, width: number, height: number) => {
    updateElementPosition(elementKey, { width, height });
  };

  // Handle element rotation
  const handleElementRotate = (elementKey: keyof AdElementsState, delta: number) => {
    const currentRotation = elements[elementKey].rotation;
    const newRotation = (currentRotation + delta) % 360;
    updateElementPosition(elementKey, { rotation: newRotation });
  };

  // Get font categories for dropdowns
  const getFontCategories = () => {
    const aiRecommendedFonts = [
      generatedContent.typography?.headline_font,
      generatedContent.typography?.body_font,
      generatedContent.recommended_font
    ].filter(Boolean) as string[];

    const brandFonts = firmDetails?.font_preferences || [];

    const standardFonts = [
      'Arial', 'Helvetica', 'Times New Roman', 'Georgia',
      'Verdana', 'Courier New', 'Tahoma', 'Comic Sans MS',
      'Impact', 'Trebuchet MS', 'Palatino', 'Garamond',
      'Montserrat', 'Roboto', 'Open Sans', 'Lato', 'Raleway'
    ];

    return {
      aiRecommended: Array.from(new Set(aiRecommendedFonts)),
      brand: Array.from(new Set(brandFonts)),
      standard: standardFonts.filter(f =>
        !aiRecommendedFonts.includes(f) && !brandFonts.includes(f)
      )
    };
  };

  // Z-index handlers
  const handleMoveToFront = () => {
    if (!selectedElement) return;
    const newOrder = ZIndexManager.moveToFront(selectedElement, zIndexOrder);
    setZIndexOrder(newOrder);
  };

  const handleMoveToBack = () => {
    if (!selectedElement) return;
    const newOrder = ZIndexManager.moveToBack(selectedElement, zIndexOrder);
    setZIndexOrder(newOrder);
  };

  const handleMoveUp = () => {
    if (!selectedElement) return;
    const newOrder = ZIndexManager.moveUp(selectedElement, zIndexOrder);
    setZIndexOrder(newOrder);
  };

  const handleMoveDown = () => {
    if (!selectedElement) return;
    const newOrder = ZIndexManager.moveDown(selectedElement, zIndexOrder);
    setZIndexOrder(newOrder);
  };

  // Template handlers
  const handleSaveTemplate = () => {
    const templateName = prompt('Enter template name:');
    if (!templateName) return;

    const template: AdTemplate = {
      id: '',
      name: templateName,
      elements: {
        logo: elements.logo,
        businessName: elements.businessName,
        headline: elements.headline,
        copy: elements.copy,
        cta: elements.cta,
        hashtags: elements.hashtags
      },
      styles: {
        businessNameFont,
        businessNameFontSize,
        businessNameColor,
        headlineFont,
        headlineFontSize,
        headlineColor,
        copyFont,
        copyFontSize,
        copyColor,
        ctaFont,
        ctaFontSize,
        ctaBackgroundColor,
        ctaTextColor,
        hashtagsFont,
        hashtagsFontSize,
        hashtagsColor
      },
      zIndexOrder,
      createdAt: new Date().toISOString()
    };

    TemplateManager.saveTemplate(template);
    setTemplates(TemplateManager.getAllTemplates());
    alert(`Template "${templateName}" saved!`);
  };

  const handleLoadTemplate = (templateId: string) => {
    const template = TemplateManager.getTemplate(templateId);
    if (!template) return;

    // Apply element positions
    setElements({
      logo: template.elements.logo,
      businessName: template.elements.businessName,
      headline: template.elements.headline,
      copy: template.elements.copy,
      cta: template.elements.cta,
      hashtags: template.elements.hashtags
    });

    // Apply styles
    setBusinessNameColor(template.styles.businessNameColor);
    setBusinessNameFont(template.styles.businessNameFont);
    setBusinessNameFontSize(template.styles.businessNameFontSize);
    setHeadlineColor(template.styles.headlineColor);
    setHeadlineFont(template.styles.headlineFont);
    setHeadlineFontSize(template.styles.headlineFontSize);
    setCopyColor(template.styles.copyColor);
    setCopyFont(template.styles.copyFont);
    setCopyFontSize(template.styles.copyFontSize);
    setCtaTextColor(template.styles.ctaTextColor);
    setCtaFont(template.styles.ctaFont);
    setCtaFontSize(template.styles.ctaFontSize);
    setCtaBackgroundColor(template.styles.ctaBackgroundColor);
    setHashtagsColor(template.styles.hashtagsColor);
    setHashtagsFont(template.styles.hashtagsFont);
    setHashtagsFontSize(template.styles.hashtagsFontSize);

    // Apply z-index order
    setZIndexOrder(template.zIndexOrder);

    setShowTemplateModal(false);
    alert(`Template "${template.name}" loaded!`);
  };

  const handleDeleteTemplate = (templateId: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this template?')) return;
    TemplateManager.deleteTemplate(templateId);
    setTemplates(TemplateManager.getAllTemplates());
  };

  // Export handlers
  const handleExportCurrent = async () => {
    if (!svgCanvasRef.current) return;
    
    try {
      const blob = await svgCanvasRef.current.exportAdGroup();
      const { width, height } = getCurrentCanvasSize();
      const filename = `ad-${activePlatform}-${width}x${height}.png`;
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export ad. Please try again.');
    }
  };

  const handleExportAll = async () => {
    if (!canvasRef.current) return;
    
    const platformConfigs = selectedPlatforms.flatMap(platform => {
      const sizes = generatedContent.platform_sizes?.[platform] ||
                   DEFAULT_PLATFORM_SIZES[platform as keyof typeof DEFAULT_PLATFORM_SIZES] ||
                   DEFAULT_PLATFORM_SIZES.Facebook;
      
      return sizes.map((size: any) => ({
        platform,
        size,
        width: size.width,
        height: size.height
      }));
    });

    await CanvasExporter.exportMultiplePlatforms(
      () => canvasRef.current,
      platformConfigs,
      'ad'
    );
  };

  // Render size and rotation controls
  const renderTransformControls = (elementKey: keyof AdElementsState) => {
    const element = elements[elementKey];
    return (
      <>
        <div className="editor-section">
          <label>Size</label>
          <div className="size-controls">
            <div className="size-input-group">
              <label>Width</label>
              <input
                type="number"
                value={element.width}
                onChange={(e) => handleElementResize(elementKey, Number(e.target.value), element.height)}
                className="size-input"
                min="50"
              />
            </div>
            <div className="size-input-group">
              <label>Height</label>
              <input
                type="number"
                value={element.height}
                onChange={(e) => handleElementResize(elementKey, element.width, Number(e.target.value))}
                className="size-input"
                min="20"
              />
            </div>
          </div>
        </div>
        <div className="editor-section">
          <label>Rotation</label>
          <div className="rotation-controls">
            <button onClick={() => handleElementRotate(elementKey, -15)} className="btn-rotate">
              <i className="bi bi-arrow-counterclockwise"></i> -15°
            </button>
            <span className="rotation-value">{element.rotation}°</span>
            <button onClick={() => handleElementRotate(elementKey, 15)} className="btn-rotate">
              <i className="bi bi-arrow-clockwise"></i> +15°
            </button>
          </div>
        </div>
      </>
    );
  };

  // Render text alignment controls
  const renderTextAlignControls = (currentAlign: 'left' | 'center' | 'right', onChange: (align: 'left' | 'center' | 'right') => void) => {
    return (
      <div className="editor-section">
        <label>Text Alignment</label>
        <div className="align-controls">
          <button
            className={`align-btn ${currentAlign === 'left' ? 'active' : ''}`}
            onClick={() => onChange('left')}
            title="Align Left"
          >
            <i className="bi bi-text-left"></i>
          </button>
          <button
            className={`align-btn ${currentAlign === 'center' ? 'active' : ''}`}
            onClick={() => onChange('center')}
            title="Align Center"
          >
            <i className="bi bi-text-center"></i>
          </button>
          <button
            className={`align-btn ${currentAlign === 'right' ? 'active' : ''}`}
            onClick={() => onChange('right')}
            title="Align Right"
          >
            <i className="bi bi-text-right"></i>
          </button>
        </div>
      </div>
    );
  };

  const renderFontSelector = (currentFont: string, onChange: (font: string) => void) => {
    const fontCategories = getFontCategories();

    return (
      <select
        value={currentFont}
        onChange={(e) => onChange(e.target.value)}
        className="style-select font-select"
      >
        {fontCategories.aiRecommended.length > 0 && (
          <optgroup label="✨ AI Recommended">
            {fontCategories.aiRecommended.map(font => (
              <option key={`ai-${font}`} value={font}>{font}</option>
            ))}
          </optgroup>
        )}
        {fontCategories.brand.length > 0 && (
          <optgroup label="🎨 Brand Fonts">
            {fontCategories.brand.map(font => (
              <option key={`brand-${font}`} value={font}>{font}</option>
            ))}
          </optgroup>
        )}
        <optgroup label="📝 Standard Fonts">
          {fontCategories.standard.map(font => (
            <option key={`std-${font}`} value={font}>{font}</option>
          ))}
        </optgroup>
      </select>
    );
  };

  // Handle hashtag operations
  const addHashtag = () => {
    setHashtags([...hashtags, '']);
  };

  const updateHashtag = (index: number, value: string) => {
    const newHashtags = [...hashtags];
    newHashtags[index] = value.startsWith('#') ? value.slice(1) : value;
    setHashtags(newHashtags);
  };

  const removeHashtag = (index: number) => {
    setHashtags(hashtags.filter((_, i) => i !== index));
  };

  // Render platform tabs
  const renderPlatformTabs = () => (
    <div className="platform-tabs">
      {selectedPlatforms.map(platform => (
        <button
          key={platform}
          className={`platform-tab ${activePlatform === platform ? 'active' : ''}`}
          onClick={() => {
            setActivePlatform(platform);
            setActiveSizeIndex(0);
          }}
        >
          {platform}
        </button>
      ))}
    </div>
  );

  // Render size tabs for current platform
  const renderSizeTabs = () => {
    const platformSizes = generatedContent.platform_sizes?.[activePlatform] ||
                         DEFAULT_PLATFORM_SIZES[activePlatform as keyof typeof DEFAULT_PLATFORM_SIZES];

    if (!platformSizes || platformSizes.length === 0) {
      return (
        <div className="size-tabs">
          <div className="platform-tab active">
            Default (1200x628)
          </div>
        </div>
      );
    }

    return (
      <div className="size-tabs">
        {platformSizes.map((size, index) => (
          <button
            key={index}
            className={`size-tab ${activeSizeIndex === index ? 'active' : ''}`}
            onClick={() => setActiveSizeIndex(index)}
          >
            {size.name}
            <span className="size-info">{size.width}x{size.height}</span>
          </button>
        ))}
      </div>
    );
  };

  // Render element editor panel
  const renderElementEditor = () => {
    if (!selectedElement) {
      return (
        <div className="no-selection">
          <p>Select an element on the canvas to edit</p>
        </div>
      );
    }

    switch (selectedElement) {
      case 'logo':
        return (
          <div className="element-editor">
            <h4>Logo</h4>
            {firmDetails?.logo_url && (
              <div className="editor-section">
                <label>Logo Image</label>
                <img src={firmDetails.logo_url} alt="Logo" className="logo-preview" />
              </div>
            )}
            {renderTransformControls('logo')}
            <div className="editor-section">
              <label>Layer Order</label>
              <div className="z-index-controls">
                <button onClick={handleMoveToFront} className="btn-layer">
                  <i className="bi bi-layer-forward"></i> To Front
                </button>
                <button onClick={handleMoveUp} className="btn-layer">
                  <i className="bi bi-arrow-up"></i> Up
                </button>
                <button onClick={handleMoveDown} className="btn-layer">
                  <i className="bi bi-arrow-down"></i> Down
                </button>
                <button onClick={handleMoveToBack} className="btn-layer">
                  <i className="bi bi-layer-backward"></i> To Back
                </button>
              </div>
            </div>
          </div>
        );

      case 'businessName':
        return (
          <div className="element-editor">
            <h4>Business Name</h4>
            <div className="editor-section">
              <label>Name</label>
              <input
                type="text"
                value={firmDetails?.business_name || firmDetails?.name || ''}
                disabled
                className="text-input"
              />
            </div>
            <div className="editor-section">
              <label>Font Family</label>
              {renderFontSelector(businessNameFont, setBusinessNameFont)}
            </div>
            <div className="editor-section">
              <label>Font Size</label>
              <select
                value={businessNameFontSize}
                onChange={(e) => setBusinessNameFontSize(e.target.value)}
                className="style-select"
              >
                <option>18px</option>
                <option>20px</option>
                <option>24px</option>
                <option>28px</option>
                <option>32px</option>
              </select>
            </div>
            <div className="editor-section">
              <label>Color</label>
              <input
                type="color"
                value={businessNameColor}
                onChange={(e) => setBusinessNameColor(e.target.value)}
                className="color-picker"
              />
            </div>
            {renderTextAlignControls(businessNameAlign, setBusinessNameAlign)}
            {renderTransformControls('businessName')}
            <div className="editor-section">
              <label>Layer Order</label>
              <div className="z-index-controls">
                <button onClick={handleMoveToFront} className="btn-layer">
                  <i className="bi bi-layer-forward"></i> To Front
                </button>
                <button onClick={handleMoveUp} className="btn-layer">
                  <i className="bi bi-arrow-up"></i> Up
                </button>
                <button onClick={handleMoveDown} className="btn-layer">
                  <i className="bi bi-arrow-down"></i> Down
                </button>
                <button onClick={handleMoveToBack} className="btn-layer">
                  <i className="bi bi-layer-backward"></i> To Back
                </button>
              </div>
            </div>
          </div>
        );

      case 'headline':
        return (
          <div className="element-editor">
            <h4>Headline</h4>
            <div className="editor-section">
              <label>Text</label>
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                className="text-input"
              />
            </div>
            <div className="editor-section">
              <label>Font Family</label>
              {renderFontSelector(headlineFont, setHeadlineFont)}
            </div>
            <div className="editor-section">
              <label>Font Size</label>
              <select
                value={headlineFontSize}
                onChange={(e) => setHeadlineFontSize(e.target.value)}
                className="style-select"
              >
                <option>24px</option>
                <option>28px</option>
                <option>32px</option>
                <option>36px</option>
                <option>40px</option>
                <option>48px</option>
              </select>
            </div>
            <div className="editor-section">
              <label>Color</label>
              <input
                type="color"
                value={headlineColor}
                onChange={(e) => setHeadlineColor(e.target.value)}
                className="color-picker"
              />
            </div>
            {renderTextAlignControls(headlineAlign, setHeadlineAlign)}
            {renderTransformControls('headline')}
            <div className="editor-section">
              <label>Layer Order</label>
              <div className="z-index-controls">
                <button onClick={handleMoveToFront} className="btn-layer">
                  <i className="bi bi-layer-forward"></i> To Front
                </button>
                <button onClick={handleMoveUp} className="btn-layer">
                  <i className="bi bi-arrow-up"></i> Up
                </button>
                <button onClick={handleMoveDown} className="btn-layer">
                  <i className="bi bi-arrow-down"></i> Down
                </button>
                <button onClick={handleMoveToBack} className="btn-layer">
                  <i className="bi bi-layer-backward"></i> To Back
                </button>
              </div>
            </div>
          </div>
        );

      case 'copy':
        return (
          <div className="element-editor">
            <h4>Ad Copy</h4>
            <div className="editor-section">
              <label>Text</label>
              <textarea
                value={adCopy}
                onChange={(e) => setAdCopy(e.target.value)}
                className="text-area"
                rows={4}
              />
            </div>
            <div className="editor-section">
              <label>Font Family</label>
              {renderFontSelector(copyFont, setCopyFont)}
            </div>
            <div className="editor-section">
              <label>Font Size</label>
              <select
                value={copyFontSize}
                onChange={(e) => setCopyFontSize(e.target.value)}
                className="style-select"
              >
                <option>12px</option>
                <option>14px</option>
                <option>16px</option>
                <option>18px</option>
                <option>20px</option>
              </select>
            </div>
            <div className="editor-section">
              <label>Color</label>
              <input
                type="color"
                value={copyColor}
                onChange={(e) => setCopyColor(e.target.value)}
                className="color-picker"
              />
            </div>
            {renderTextAlignControls(copyAlign, setCopyAlign)}
            {renderTransformControls('copy')}
            <div className="editor-section">
              <label>Layer Order</label>
              <div className="z-index-controls">
                <button onClick={handleMoveToFront} className="btn-layer">
                  <i className="bi bi-layer-forward"></i> To Front
                </button>
                <button onClick={handleMoveUp} className="btn-layer">
                  <i className="bi bi-arrow-up"></i> Up
                </button>
                <button onClick={handleMoveDown} className="btn-layer">
                  <i className="bi bi-arrow-down"></i> Down
                </button>
                <button onClick={handleMoveToBack} className="btn-layer">
                  <i className="bi bi-layer-backward"></i> To Back
                </button>
              </div>
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="element-editor">
            <h4>Call-to-Action</h4>
            <div className="editor-section">
              <label>Text</label>
              <input
                type="text"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                className="text-input"
              />
            </div>
            <div className="editor-section">
              <label>Font Family</label>
              {renderFontSelector(ctaFont, setCtaFont)}
            </div>
            <div className="editor-section">
              <label>Font Size</label>
              <select
                value={ctaFontSize}
                onChange={(e) => setCtaFontSize(e.target.value)}
                className="style-select"
              >
                <option>14px</option>
                <option>16px</option>
                <option>18px</option>
                <option>20px</option>
              </select>
            </div>
            <div className="editor-section">
              <label>Button Color</label>
              <input
                type="color"
                value={ctaBackgroundColor}
                onChange={(e) => setCtaBackgroundColor(e.target.value)}
                className="color-picker"
              />
            </div>
            <div className="editor-section">
              <label>Text Color</label>
              <input
                type="color"
                value={ctaTextColor}
                onChange={(e) => setCtaTextColor(e.target.value)}
                className="color-picker"
              />
            </div>
            {renderTransformControls('cta')}
            <div className="editor-section">
              <label>Layer Order</label>
              <div className="z-index-controls">
                <button onClick={handleMoveToFront} className="btn-layer">
                  <i className="bi bi-layer-forward"></i> To Front
                </button>
                <button onClick={handleMoveUp} className="btn-layer">
                  <i className="bi bi-arrow-up"></i> Up
                </button>
                <button onClick={handleMoveDown} className="btn-layer">
                  <i className="bi bi-arrow-down"></i> Down
                </button>
                <button onClick={handleMoveToBack} className="btn-layer">
                  <i className="bi bi-layer-backward"></i> To Back
                </button>
              </div>
            </div>
          </div>
        );

      case 'hashtags':
        return (
          <div className="element-editor">
            <h4>Hashtags</h4>
            <div className="editor-section">
              <label>Hashtags</label>
              {hashtags.map((tag, index) => (
                <div key={index} className="hashtag-input-row">
                  <input
                    type="text"
                    value={tag}
                    onChange={(e) => updateHashtag(index, e.target.value)}
                    placeholder="hashtag"
                    className="text-input"
                  />
                  <button
                    onClick={() => removeHashtag(index)}
                    className="btn-remove"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
              ))}
              <button onClick={addHashtag} className="btn-add">
                <i className="bi bi-plus"></i> Add Hashtag
              </button>
            </div>
            <div className="editor-section">
              <label>Font Family</label>
              {renderFontSelector(hashtagsFont, setHashtagsFont)}
            </div>
            <div className="editor-section">
              <label>Font Size</label>
              <select
                value={hashtagsFontSize}
                onChange={(e) => setHashtagsFontSize(e.target.value)}
                className="style-select"
              >
                <option>12px</option>
                <option>14px</option>
                <option>16px</option>
                <option>18px</option>
              </select>
            </div>
            <div className="editor-section">
              <label>Color</label>
              <input
                type="color"
                value={hashtagsColor}
                onChange={(e) => setHashtagsColor(e.target.value)}
                className="color-picker"
              />
            </div>
            {renderTextAlignControls(hashtagsAlign, setHashtagsAlign)}
            {renderTransformControls('hashtags')}
            <div className="editor-section">
              <label>Layer Order</label>
              <div className="z-index-controls">
                <button onClick={handleMoveToFront} className="btn-layer">
                  <i className="bi bi-layer-forward"></i> To Front
                </button>
                <button onClick={handleMoveUp} className="btn-layer">
                  <i className="bi bi-arrow-up"></i> Up
                </button>
                <button onClick={handleMoveDown} className="btn-layer">
                  <i className="bi bi-arrow-down"></i> Down
                </button>
                <button onClick={handleMoveToBack} className="btn-layer">
                  <i className="bi bi-layer-backward"></i> To Back
                </button>
              </div>
            </div>
          </div>
        );

      case 'background':
        return (
          <div className="element-editor">
            <h4>Background Image</h4>
            {recommendedImages.length > 0 && (
              <div className="editor-section">
                <label>AI Recommended Images</label>
                <div className="image-gallery">
                  {recommendedImages.map((img, idx) => (
                    <div
                      key={`rec-${idx}`}
                      className={`gallery-image ${selectedImage === img.url ? 'selected' : ''}`}
                      onClick={() => onImageSelect(img.url)}
                    >
                      <img src={img.url} alt={img.alt_text || `Recommended ${idx + 1}`} />
                      {img.score && (
                        <div className="image-score">Score: {Math.round(img.score)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="editor-section">
              <label>Sample Images</label>
              {isLoadingImages ? (
                <div className="loading-images">Loading images...</div>
              ) : sampleImages.length > 0 ? (
                <div className="image-gallery">
                  {sampleImages.map((img, idx) => (
                    <div
                      key={`sample-${idx}`}
                      className={`gallery-image ${selectedImage === img.url ? 'selected' : ''}`}
                      onClick={() => onImageSelect(img.url)}
                    >
                      <img src={img.url} alt={img.alt_text || `Sample ${idx + 1}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-images">No sample images available</p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const currentImage = selectedImage || (recommendedImages.length > 0 ? recommendedImages[0].url : null);

  // Convert element states to SVG elements format
  const svgElements = useMemo(() => {
    const result = [];

    // Logo
    if (firmDetails?.logo_url) {
      result.push({
        id: 'logo',
        type: 'image' as const,
        x: elements.logo.x,
        y: elements.logo.y,
        width: elements.logo.width,
        height: elements.logo.height,
        rotation: elements.logo.rotation,
        imageUrl: firmDetails.logo_url
      });
    }

    // Business Name
    result.push({
      id: 'businessName',
      type: 'text' as const,
      x: elements.businessName.x,
      y: elements.businessName.y,
      width: elements.businessName.width,
      height: elements.businessName.height,
      rotation: elements.businessName.rotation,
      content: firmDetails?.business_name || firmDetails?.name || 'Business Name',
      fontSize: parseInt(businessNameFontSize),
      fontFamily: businessNameFont,
      fill: businessNameColor,
      textAlign: businessNameAlign
    });

    // Headline
    if (headline) {
      result.push({
        id: 'headline',
        type: 'text' as const,
        x: elements.headline.x,
        y: elements.headline.y,
        width: elements.headline.width,
        height: elements.headline.height,
        rotation: elements.headline.rotation,
        content: headline,
        fontSize: parseInt(headlineFontSize),
        fontFamily: headlineFont,
        fill: headlineColor,
        textAlign: headlineAlign
      });
    }

    // Copy
    if (adCopy) {
      result.push({
        id: 'copy',
        type: 'text' as const,
        x: elements.copy.x,
        y: elements.copy.y,
        width: elements.copy.width,
        height: elements.copy.height,
        rotation: elements.copy.rotation,
        content: adCopy,
        fontSize: parseInt(copyFontSize),
        fontFamily: copyFont,
        fill: copyColor,
        textAlign: copyAlign
      });
    }

    // CTA
    if (ctaText) {
      result.push({
        id: 'cta',
        type: 'rect' as const,
        x: elements.cta.x,
        y: elements.cta.y,
        width: elements.cta.width,
        height: elements.cta.height,
        rotation: elements.cta.rotation,
        content: ctaText,
        fontSize: parseInt(ctaFontSize),
        fontFamily: ctaFont,
        fill: ctaBackgroundColor
      });
    }

    // Hashtags
    if (hashtags.length > 0) {
      result.push({
        id: 'hashtags',
        type: 'text' as const,
        x: elements.hashtags.x,
        y: elements.hashtags.y,
        width: elements.hashtags.width,
        height: elements.hashtags.height,
        rotation: elements.hashtags.rotation,
        content: hashtags.map(tag => `#${tag}`).join(' '),
        fontSize: parseInt(hashtagsFontSize),
        fontFamily: hashtagsFont,
        fill: hashtagsColor,
        textAlign: hashtagsAlign
      });
    }

    return result;
  }, [elements, firmDetails, businessNameFont, businessNameFontSize, businessNameColor, businessNameAlign,
      headline, headlineFont, headlineFontSize, headlineColor, headlineAlign,
      adCopy, copyFont, copyFontSize, copyColor, copyAlign,
      ctaText, ctaFont, ctaFontSize, ctaBackgroundColor,
      hashtags, hashtagsFont, hashtagsFontSize, hashtagsColor, hashtagsAlign]);

  const handleSvgElementUpdate = (id: string, updates: Partial<ElementPosition>) => {
    setElements(prev => ({
      ...prev,
      [id]: { ...prev[id as keyof AdElementsState], ...updates }
    }));
  };

  const handleSvgElementSelect = (id: string) => {
    setSelectedElement(id || null);
  };

  return (
    <div className="banner-editor-v2">
      <div className="editor-canvas-area">
        {renderPlatformTabs()}
        {renderSizeTabs()}

        <div className="canvas-container" ref={containerRef}>
          <div ref={canvasRef}>
            <SvgAdCanvas
              ref={svgCanvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              backgroundImage={currentImage}
              elements={svgElements}
              selectedElementId={selectedElement}
              onElementUpdate={handleSvgElementUpdate}
              onElementSelect={handleSvgElementSelect}
            />
          </div>
        </div>

        <div className="canvas-sticky-actions">
          <button className="action-btn secondary" onClick={handleExportCurrent}>
            <i className="bi bi-download"></i> Export Current
          </button>
          <button className="action-btn secondary" onClick={handleExportAll}>
            <i className="bi bi-collection"></i> Export All
          </button>
          {/* <button className="action-btn secondary" onClick={handleSaveTemplate}>
            <i className="bi bi-save"></i> Save Template
          </button>
          <button className="action-btn secondary" onClick={() => setShowTemplateModal(true)}>
            <i className="bi bi-folder-open"></i> Load Template
          </button> */}
          <button className="action-btn primary" onClick={onPublish}>
            <i className="bi bi-check-circle"></i> Publish
          </button>
        </div>
      </div>

      <div className="editor-sidebar">
        <div className="element-selector">
          <h3>Elements</h3>
          <div className="element-list">
            <button
              className={`element-item ${selectedElement === 'background' ? 'active' : ''}`}
              onClick={() => setSelectedElement('background')}
            >
              <i className="bi bi-image"></i>
              <span>Background</span>
            </button>
            <button
              className={`element-item ${selectedElement === 'logo' ? 'active' : ''}`}
              onClick={() => setSelectedElement('logo')}
            >
              <i className="bi bi-badge-tm"></i>
              <span>Logo</span>
            </button>
            <button
              className={`element-item ${selectedElement === 'businessName' ? 'active' : ''}`}
              onClick={() => setSelectedElement('businessName')}
            >
              <i className="bi bi-building"></i>
              <span>Business Name</span>
            </button>
            <button
              className={`element-item ${selectedElement === 'headline' ? 'active' : ''}`}
              onClick={() => setSelectedElement('headline')}
            >
              <i className="bi bi-type-h1"></i>
              <span>Headline</span>
            </button>
            <button
              className={`element-item ${selectedElement === 'copy' ? 'active' : ''}`}
              onClick={() => setSelectedElement('copy')}
            >
              <i className="bi bi-text-paragraph"></i>
              <span>Ad Copy</span>
            </button>
            <button
              className={`element-item ${selectedElement === 'cta' ? 'active' : ''}`}
              onClick={() => setSelectedElement('cta')}
            >
              <i className="bi bi-cursor"></i>
              <span>Call-to-Action</span>
            </button>
            <button
              className={`element-item ${selectedElement === 'hashtags' ? 'active' : ''}`}
              onClick={() => setSelectedElement('hashtags')}
            >
              <i className="bi bi-hash"></i>
              <span>Hashtags</span>
            </button>
          </div>
        </div>

        <div className="editor-content">
          {renderElementEditor()}
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="template-modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="template-modal-header">
              <h3>Load Template</h3>
              <button className="btn-close" onClick={() => setShowTemplateModal(false)}>
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="template-modal-body">
              {templates.length === 0 ? (
                <p className="no-templates">No saved templates yet. Save your current design to create a template!</p>
              ) : (
                <div className="template-list">
                  {templates.map(template => (
                    <div key={template.id} className="template-item">
                      <div className="template-info">
                        <h4>{template.name}</h4>
                        <p className="template-date">
                          {new Date(template.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="template-actions">
                        <button 
                          className="btn-load" 
                          onClick={() => handleLoadTemplate(template.id)}
                        >
                          <i className="bi bi-check-circle"></i> Load
                        </button>
                        <button 
                          className="btn-delete" 
                          onClick={() => handleDeleteTemplate(template.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerEditorV2;
